import React from 'react';
import { ArrowRight, ShieldCheck, Truck, Leaf, Star, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Bubbles } from './Decor';
import { useStore } from '../context/StoreContext';

const Hero = () => {
  const { settings } = useStore();

  return (
    <section id="home" className="relative overflow-hidden fbd-gradient pt-24 md:pt-28 pb-16 md:pb-24">
      {/* decorative blobs */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-[#f5b400]/10 blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {settings.saleActive && settings.saleText && (
          <div className="inline-flex items-center gap-2 bg-[#e11d1d]/10 text-[#e11d1d] px-4 py-1.5 rounded-full text-sm font-semibold mb-6 animate-fade-up">
            <Star className="h-4 w-4 fill-[#e11d1d]" /> {settings.saleText}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="animate-fade-up">
            <p className="text-primary font-semibold tracking-wide uppercase text-sm mb-4">
              Premium Quality · High Performance
            </p>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
              Everyday Hygiene, <span className="brand-green">Simple</span> &{' '}
              <span className="brand-blue">Effective</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl">
              We combine tough cleaning power with gentle care to keep your family safe and your spaces
              spotless. Shop our premium 5L range with Cash on Delivery.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                asChild
                size="lg"
                data-testid="hero-shop-btn" className="btn-shimmer bg-primary hover:bg-primary/90 text-white font-semibold text-base px-7 h-13 py-6"
              >
                <a href="#products">
                  Shop Products <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-primary/30 text-foreground hover:bg-primary/5 font-semibold text-base px-7 py-6"
              >
                <a href="#contact">Contact Us</a>
              </Button>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              {[
                { icon: ShieldCheck, label: '99.9% Germ Protection', color: 'text-primary' },
                { icon: Truck, label: 'Cash on Delivery', color: 'brand-blue' },
                { icon: Leaf, label: 'Long-Lasting Freshness', color: 'brand-green' },
              ].map((f, i) => (
                <div key={i} className="flex flex-col items-start gap-2">
                  <f.icon className={`h-6 w-6 ${f.color}`} />
                  <span className="text-sm font-medium text-foreground/80">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative flex justify-center animate-fade-up">
            {/* layered glow pedestal */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-72 w-72 md:h-[26rem] md:w-[26rem] rounded-full bg-[radial-gradient(circle_at_50%_45%,rgba(21,154,59,0.35)_0%,rgba(21,154,59,0.12)_45%,rgba(21,154,59,0)_72%)]" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-48 w-48 md:h-72 md:w-72 rounded-full bg-white/70 blur-2xl" />
            </div>

            {/* floating clipart */}
            <Bubbles count={9} className="z-0" />
            <Sparkles className="absolute top-6 right-10 h-8 w-8 text-[#f5b400] sparkle-twinkle z-20" />
            <Sparkles className="absolute bottom-24 left-8 h-6 w-6 text-primary sparkle-twinkle z-20" style={{ animationDelay: '1s' }} />
            <Sparkles className="absolute top-1/3 left-2 h-5 w-5 text-[#1a66c9] sparkle-twinkle z-20" style={{ animationDelay: '1.8s' }} />

            <img
              src="/assets/gen/phenyl.png"
              alt="FBD Cleaning Phenyl"
              className="relative z-10 h-[340px] md:h-[470px] w-auto object-contain drop-shadow-[0_28px_34px_rgba(0,0,0,0.28)] animate-float"
            />
            <div className="absolute z-30 bottom-6 -left-2 md:left-6 glass-card border border-white/60 rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 animate-fade-up">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-[#f5b400] text-[#f5b400]" />
                ))}
              </div>
              <div>
                <p className="text-sm font-bold text-foreground leading-none">4.8 / 5</p>
                <p className="text-xs text-muted-foreground">500+ happy homes</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
